import type { Chat, ChatMessage, ClientState } from "@rhema/client";
import { type Format, type MentionName, toHtml, toPlainText } from "@rhema/format";
import { cn, focusRing, RichText, type RichTextProps, UiLink } from "@rhema/ui";
import { type ReactNode, useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { COPY_BLOCKED, copyText } from "../menus/clipboard";
import { openLink } from "../platform/links";
import { useClientState, useSession } from "../session/session";
import { homeSpaceId, inSpace, spaceHref } from "../spaces/data";

// Showing message bodies with their formatting, and the plain text of them where formatting has
// no place: previews, reply quotes, notifications, search and Queravel.

/** How a message's current words are written: the edit's format once it was edited. */
export function formatOf(m: ChatMessage): Format {
  const c = m.content;
  if (m.edited !== null) return m.editedFormat;
  return c?.type === "text" ? c.format : undefined;
}

/** The names the message mentions, as the body writes them after the @. */
export function mentionNames(chat: Pick<Chat, "members"> | undefined, ids: readonly string[] | undefined) {
  const out: MentionName[] = [];
  for (const id of ids ?? []) {
    const name = chat?.members.find((x) => x.user.id === id)?.user.display_name;
    if (name) out.push({ id, name });
  }
  return out;
}

/** The ids a text message or caption mentions. */
export function mentionIdsOf(m: ChatMessage): string[] {
  const c = m.content;
  return c?.type === "text" || c?.type === "media" ? (c.mentions ?? []) : [];
}

/** A text body as plain words. Formatting marks go, and the text they held stays. */
export function plainOf(body: string, format: Format, withUrls = false): string {
  return toPlainText(body, format, withUrls ? { withUrls: true } : {});
}

/** Plain text and HTML of a message's words, for Copy text. */
export function clipboardOf(m: ChatMessage, words: string, chat?: Chat): { text: string; html: string } {
  const format = formatOf(m);
  return { text: words, html: toHtml(words, format, mentionNames(chat, mentionIdsOf(m))) };
}

/**
 * The channel a #name in a message stands for: one the reader is in, in the space on screen first,
 * then any of theirs with that name. Channels the reader cannot open give nothing, so the name
 * stays as plain text.
 */
export function channelNamed(
  state: Pick<ClientState, "chats" | "spaces">,
  name: string,
  spaceId?: string,
): { chat: Chat; spaceId: string } | null {
  const wanted = name.toLowerCase();
  const matches = Object.values(state.chats).filter(
    (c) => c.kind === "channel" && c.title.toLowerCase() === wanted,
  );
  if (!matches.length) return null;
  const here = spaceId ? matches.find((c) => inSpace(c, spaceId)) : undefined;
  const chat = here ?? matches[0];
  if (!chat) return null;
  return { chat, spaceId: here && spaceId ? spaceId : homeSpaceId(state as ClientState, chat) };
}

/** What RichText needs from the app. */
export type RichRender = Pick<RichTextProps, "openExternal" | "renderChannel" | "copy">;

const channelClass = cn("rounded-sm font-semibold text-blue no-underline hover:underline", focusRing);

/**
 * Everything RichText needs from the app for one chat: links that open through the platform, a
 * Copy button that reaches the clipboard in every shell, and #channel names that open the channel
 * for people who can see it.
 */
export function useRichRender(chat?: Chat): RichRender {
  const { platform } = useSession();
  const state = useClientState();
  const { spaceId: routeSpace } = useParams();
  const spaceId =
    routeSpace || (chat?.kind === "channel" ? homeSpaceId(state, chat) : (chat?.space_id ?? undefined));

  const openExternal = useCallback((href: string) => openLink(platform, href), [platform]);
  const copy = useCallback(async (text: string) => {
    const note = await copyText(text);
    if (note === COPY_BLOCKED) throw new Error(note);
  }, []);

  // The channels change rarely, so the renderer changes only when they do, and messages that
  // name no channel are not drawn again on every update.
  const channels = Object.values(state.chats).filter((c) => c.kind === "channel");
  const signature = channels.map((c) => `${c.id}:${c.title}:${c.space_id}`).join("|");
  const spaces = state.spaces;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the signature stands for the channels
  const renderChannel = useMemo(() => {
    const lookup = { chats: Object.fromEntries(channels.map((c) => [c.id, c])), spaces };
    return (name: string): ReactNode => {
      const found = channelNamed(lookup, name, spaceId);
      if (!found) return `#${name}`;
      return (
        <UiLink
          href={spaceHref(found.spaceId, found.chat.id)}
          className={channelClass}
          onClick={(e) => e.stopPropagation()}
        >
          #{name}
        </UiLink>
      );
    };
  }, [signature, spaces, spaceId]);

  return useMemo(() => ({ openExternal, renderChannel, copy }), [openExternal, renderChannel, copy]);
}

/** A message's words with their formatting, as the chat shows them. */
export function MessageText({
  m,
  chat,
  render,
  className,
}: {
  m: ChatMessage;
  chat: Chat;
  render: RichRender;
  className?: string;
}) {
  const c = m.content;
  const body = c?.type === "text" ? (m.edited ?? c.body) : "";
  const ids = mentionIdsOf(m);
  const key = ids.join();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the ids and members stand for the names
  const mentions = useMemo(() => mentionNames(chat, ids), [key, chat.members]);
  return <RichText body={body} format={formatOf(m)} mentions={mentions} {...render} className={className} />;
}
